
import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function SterilePreparation() {
  const [activeTab, setActiveTab] = useState('equipment');

  const equipmentContent = [
    {
      title: 'クリーンベンチ',
      description: '無菌調剤に必要な基本設備',
      features: ['HEPA フィルター搭載', '垂直層流方式', '作業面の清浄度維持', '定期的なメンテナンス'],
      icon: 'ri-microscope-line',
      color: 'bg-blue-50 text-blue-600'
    },
    {
      title: 'アイソレーター',
      description: '高度な無菌環境を提供',
      features: ['完全密閉型', '陰圧・陽圧制御', 'グローブボックス操作', '最高レベルの清浄度'],
      icon: 'ri-shield-line',
      color: 'bg-green-50 text-green-600'
    },
    {
      title: '安全キャビネット',
      description: '抗がん剤等の危険薬物調剤用',
      features: ['排気処理システム', '作業者保護', '環境保護', '薬物封じ込め'],
      icon: 'ri-shield-check-line',
      color: 'bg-red-50 text-red-600'
    }
  ];

  const procedureContent = [
    {
      title: '手洗い・消毒',
      description: '無菌操作の基本となる手技',
      steps: ['石鹸による手洗い', 'アルコール消毒', '無菌手袋装着', '定期的な再消毒'],
      icon: 'ri-hand-sanitizer-line',
      color: 'bg-cyan-50 text-cyan-600'
    },
    {
      title: '無菌操作技術',
      description: '汚染を防ぐ調剤手技',
      steps: ['無菌野の設定', '器具の無菌化', '適切な操作手順', '汚染防止対策'],
      icon: 'ri-hand-heart-line',
      color: 'bg-purple-50 text-purple-600'
    },
    {
      title: '品質管理',
      description: '調剤品の品質確保',
      steps: ['外観検査', '微生物試験', '無菌試験', '記録・文書化'],
      icon: 'ri-file-check-line',
      color: 'bg-orange-50 text-orange-600'
    }
  ];

  const medicationContent = [
    {
      title: '高カロリー輸液',
      description: '中心静脈栄養療法用',
      examples: ['TPN製剤', 'アミノ酸製剤', 'ビタミン製剤', '微量元素製剤'],
      icon: 'ri-drop-line',
      color: 'bg-teal-50 text-teal-600'
    },
    {
      title: '抗がん剤',
      description: 'がん化学療法用薬剤',
      examples: ['注射用抗がん剤', '分子標的薬', '免疫チェックポイント阻害薬', '支持療法薬'],
      icon: 'ri-medicine-bottle-line',
      color: 'bg-pink-50 text-pink-600'
    },
    {
      title: '抗菌薬',
      description: '感染症治療用薬剤',
      examples: ['β-ラクタム系', 'アミノグリコシド系', 'グリコペプチド系', '抗真菌薬'],
      icon: 'ri-virus-line',
      color: 'bg-yellow-50 text-yellow-600'
    }
  ];

  const getContentByTab = (tab: string) => {
    switch (tab) {
      case 'equipment': return equipmentContent;
      case 'procedure': return procedureContent;
      case 'medication': return medicationContent;
      default: return equipmentContent;
    }
  };

  const currentContent = getContentByTab(activeTab);

  const safetyGuidelines = [
    {
      title: '作業環境の管理',
      points: [
        '清浄度クラス ISO 5 以上の維持',
        '温度・湿度の適切な管理',
        '定期的な環境モニタリング',
        '清掃・消毒の徹底'
      ]
    },
    {
      title: '作業者の安全対策',
      points: [
        '適切な防護具の着用',
        '定期的な健康診断',
        '教育・訓練の実施',
        '曝露防止対策'
      ]
    },
    {
      title: '品質保証',
      points: [
        '標準作業手順書の整備',
        '記録・文書化の徹底',
        '定期的な品質監査',
        '継続的な改善活動'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="flex items-center">
                <div className="w-10 h-10 mr-3 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-lg transform rotate-12 shadow-lg"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-fill text-white text-lg"></i>
                  </div>
                </div>
                <div className="text-xl font-bold text-teal-600" style={{ fontFamily: '"Pacifico", serif' }}>
                  みんなの薬局～Pharmacare～
                </div>
              </Link>
            </div>
            <nav className="hidden md:flex space-x-8">
              <Link to="/services" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">サービス</Link>
              <Link to="/health-support" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">健康サポート</Link>
              <Link to="/pharmacist-resources" className="text-teal-600 hover:text-teal-700 font-medium whitespace-nowrap cursor-pointer">薬剤師向け</Link>
              <Link to="/information" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">情報提供</Link>
            </nav>
            <button className="md:hidden p-2">
              <i className="ri-menu-line text-xl"></i>
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-cyan-50 to-blue-50 py-16">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/c0bd8caef512db5a681cb3e551fe2181.jpg"
            alt="無菌調剤室"
            className="w-full h-full object-cover object-top opacity-20"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            <span className="text-cyan-600">無菌調剤</span>ガイド
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            安全で確実な無菌調剤のための設備・手技・管理方法
          </p>
        </div>
      </section>

      {/* Tab Navigation */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 justify-center">
            <button
              onClick={() => setActiveTab('equipment')}
              className={`px-6 py-3 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'equipment'
                  ? 'bg-cyan-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-cyan-50 hover:text-cyan-600'
              }`}
            >
              設備・機器
            </button>
            <button
              onClick={() => setActiveTab('procedure')}
              className={`px-6 py-3 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'procedure'
                  ? 'bg-cyan-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-cyan-50 hover:text-cyan-600'
              }`}
            >
              操作手順
            </button>
            <button
              onClick={() => setActiveTab('medication')}
              className={`px-6 py-3 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'medication'
                  ? 'bg-cyan-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-cyan-50 hover:text-cyan-600'
              }`}
            >
              取扱い薬剤
            </button>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {currentContent.map((item, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow p-8 border border-gray-100">
                <div className={`w-16 h-16 rounded-xl ${item.color} flex items-center justify-center mb-6`}>
                  <i className={`${item.icon} text-2xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{item.title}</h3>
                <p className="text-gray-600 mb-6">{item.description}</p>
                <ul className="space-y-2">
                  {(item.features || item.steps || item.examples)?.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-700">
                      <i className="ri-check-line text-cyan-600 mr-2"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Safety Guidelines */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">安全管理ガイドライン</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {safetyGuidelines.map((guideline, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 border border-gray-100">
                <h3 className="text-xl font-bold text-gray-900 mb-6">{guideline.title}</h3>
                <ul className="space-y-3">
                  {guideline.points.map((point, idx) => (
                    <li key={idx} className="flex items-start">
                      <div className="w-2 h-2 bg-cyan-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <span className="text-gray-700">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            無菌調剤の重要性
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            患者さんの安全を守るため、適切な無菌調剤技術の習得が重要です
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 mr-2 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg transform rotate-12"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-300 to-emerald-300 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-fill text-white text-sm"></i>
                  </div>
                </div>
                <div className="text-xl font-bold text-teal-400" style={{ fontFamily: '"Pacifico", serif' }}>
                  みんなの薬局～Pharmacare～
                </div>
              </div>
              <p className="text-gray-400">
                患者さんと薬剤師をつなぐ総合医療情報プラットフォーム
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">無菌調剤</h3>
              <ul className="space-y-2 text-gray-400">
                <li><button onClick={() => setActiveTab('equipment')} className="hover:text-white cursor-pointer">設備・機器</button></li>
                <li><button onClick={() => setActiveTab('procedure')} className="hover:text-white cursor-pointer">操作手順</button></li>
                <li><button onClick={() => setActiveTab('medication')} className="hover:text-white cursor-pointer">取扱い薬剤</button></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">薬剤師向け</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/pharmacist-resources" className="hover:text-white cursor-pointer">専門リソース</Link></li>
                <li><Link to="/specialty-pharmacy" className="hover:text-white cursor-pointer">専門薬局</Link></li>
                <li><Link to="/home-care" className="hover:text-white cursor-pointer">在宅医療</Link></li>
                <li><Link to="/dx" className="hover:text-white cursor-pointer">薬局DX</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">情報・リンク</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/information" className="hover:text-white cursor-pointer">公的機関リンク</Link></li>
                <li><Link to="/guidelines" className="hover:text-white cursor-pointer">疾患ガイドライン</Link></li>
                <li><Link to="/career" className="hover:text-white cursor-pointer">転職情報</Link></li>
                <li><Link to="/entertainment" className="hover:text-white cursor-pointer">エンタメ</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 みんなの薬局～Pharmacare～. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
